import React from 'react';
import { Box, Card, CardContent, CardHeader, IconButton, Paper, Switch, Typography, Button } from '@mui/material';
import {
  DragIndicator as DragIcon, KeyboardArrowDown as TurunIcon, KeyboardArrowUp as NaikIcon, RestartAlt as ResetIcon,
} from '@mui/icons-material';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { URUTAN_BAWAAN, URUTAN_TIKET_BAWAAN } from '../struk/strukLayout';

// Skema = isi daftar yang bisa diurutkan: nama blok & baris, baris yang punya sakelar, sakelar tingkat blok, petunjuk.
export const SKEMA_STRUK = {
  judul: 'Susunan struk',
  bawaan: URUTAN_BAWAAN,
  labelBlok: {
    kepala: 'Kepala struk',
    info: 'Info transaksi',
    item: 'Daftar item & catatan pesanan',
    ringkasan: 'Rincian & total pembayaran',
    kaki: 'Catatan kaki',
  },
  labelBaris: {
    logo: 'Logo',
    nama: 'Nama cabang',
    telepon: 'Nomor telepon',
    wifi: 'WiFi',
    teksA: 'Teks A',
    teksB: 'Teks B',
    antrian: 'Nomor antrean',
    pembayaran: 'Metode pembayaran',
    pelanggan: 'Nama pelanggan',
    kasir: 'Nama kasir',
    waktu: 'Tanggal & jam',
    orderId: 'ID pesanan',
    meja: 'Meja / nama takeaway',
    promo: 'Promo',
    ppn: 'Subtotal & PPN',
    total: 'Total',
    bayar: 'Uang diterima, kembalian / rincian bayar bagi',
  },
  // Baris yang punya sakelar tampil/sembunyi (kunci sama dengan strukTampil di backend).
  punyaSakelar: ['logo', 'telepon', 'wifi', 'antrian', 'pelanggan', 'kasir', 'waktu', 'orderId', 'meja'],
  sakelarBlok: { item: { kunci: 'catatan', label: 'Tampilkan catatan pesanan & catatan item' } },
  petunjukBlok: { kaki: 'Isinya diatur di kartu "Catatan kaki".' },
};

export const SKEMA_TIKET = {
  judul: 'Susunan tiket',
  bawaan: URUTAN_TIKET_BAWAAN,
  labelBlok: {
    kepala: 'Kepala tiket',
    info: 'Info pesanan',
    item: 'Daftar item',
    catatan: 'Catatan pesanan',
    kaki: 'Pesan penutup',
  },
  labelBaris: {
    nama: 'Nama cabang',
    judul: 'Judul tiket',
    antrian: 'Nomor antrean',
    orderId: 'ID pesanan',
    pelanggan: 'Nama pelanggan',
    meja: 'Meja / nama takeaway',
    waktu: 'Tanggal & jam',
    kasir: 'Nama kasir',
  },
  punyaSakelar: ['nama', 'judul', 'antrian', 'orderId', 'pelanggan', 'meja', 'waktu', 'kasir'],
  sakelarBlok: {
    item: { kunci: 'catatanItem', label: 'Tampilkan catatan item' },
    catatan: { kunci: 'catatan', label: 'Tampilkan catatan pesanan' },
    kaki: { kunci: 'penutup', label: 'Cetak pesan penutup' },
  },
  petunjukBlok: {},
};

const pegangan = { cursor: 'grab', touchAction: 'none', display: 'flex', alignItems: 'center', color: 'text.secondary' };

/** Satu elemen yang bisa digeser; `render` menerima properti pegangan untuk ikon geser. */
const Sortable = ({ id, render }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{ opacity: isDragging ? 0.6 : 1, position: 'relative', zIndex: isDragging ? 2 : 'auto' }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {render({ ...attributes, ...listeners })}
    </Box>
  );
};

/** Daftar vertikal yang bisa diurutkan dengan geser (mouse/sentuh/keyboard). */
const DaftarSortable = ({ items, onChange, render }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const pindah = (id, selisih) => {
    const dari = items.indexOf(id);
    const ke = dari + selisih;
    if (ke >= 0 && ke < items.length) onChange(arrayMove(items, dari, ke));
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) onChange(arrayMove(items, items.indexOf(active.id), items.indexOf(over.id)));
      }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        {items.map((id, i) => (
          <Sortable
            key={id}
            id={id}
            render={(p) => render(id, p, {
              naik: i > 0 ? () => pindah(id, -1) : null,
              turun: i < items.length - 1 ? () => pindah(id, 1) : null,
            })}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

const TombolGeser = ({ aksi, arah }) => (
  <IconButton size="small" onClick={aksi || undefined} disabled={!aksi} aria-label={arah === 'naik' ? 'Naikkan' : 'Turunkan'}>
    {arah === 'naik' ? <NaikIcon fontSize="small" /> : <TurunIcon fontSize="small" />}
  </IconButton>
);

/**
 * Susunan struk/tiket: urutan blok dan urutan baris di dalam blok (geser atau tombol panah), plus sakelar tampil/sembunyi.
 * Pembatas (garis) mengikuti blok & digabung otomatis oleh mesin struk. [skema] menentukan isi (struk pelanggan / tiket).
 */
export const SusunanStruk = ({ skema = SKEMA_STRUK, urutan, setUrutan, tampil, setTampil, logoStruk, setLogoStruk, adaLogo, disabled }) => {
  const { labelBlok, labelBaris, punyaSakelar, sakelarBlok, petunjukBlok } = skema;
  const isiBlok = (blok, geser) => {
    const baris = urutan[blok] || [];
    const ubah = (daftar) => setUrutan((p) => ({ ...p, [blok]: daftar }));
    const sakelar = sakelarBlok[blok];
    return (
      <Paper variant="outlined" sx={{ mb: 1, p: 1, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={pegangan} {...geser.pegangan} aria-label={`Geser blok ${labelBlok[blok]}`}><DragIcon /></Box>
          <Typography sx={{ flex: 1, fontWeight: 600 }}>{labelBlok[blok]}</Typography>
          <TombolGeser aksi={geser.naik} arah="naik" />
          <TombolGeser aksi={geser.turun} arah="turun" />
        </Box>
        {baris.length > 0 && (
          <Box sx={{ pl: 3.5, pt: 0.5 }}>
            <DaftarSortable
              items={baris}
              onChange={ubah}
              render={(kunci, p, g) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={pegangan} {...p} aria-label={`Geser ${labelBaris[kunci]}`}><DragIcon fontSize="small" /></Box>
                  <Typography variant="body2" sx={{ flex: 1 }}>{labelBaris[kunci]}</Typography>
                  {punyaSakelar.includes(kunci) && (
                    <Switch
                      size="small"
                      disabled={disabled}
                      checked={kunci === 'logo' && setLogoStruk ? logoStruk && tampil.logo !== false : tampil[kunci] !== false}
                      onChange={(e) => {
                        if (kunci === 'logo' && setLogoStruk) setLogoStruk(e.target.checked);
                        setTampil((t) => ({ ...t, [kunci]: e.target.checked }));
                      }}
                    />
                  )}
                  <TombolGeser aksi={g.naik} arah="naik" />
                  <TombolGeser aksi={g.turun} arah="turun" />
                </Box>
              )}
            />
          </Box>
        )}
        {sakelar && (
          <Box sx={{ pl: 3.5, display: 'flex', alignItems: 'center', borderTop: baris.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
            <Typography variant="body2" sx={{ flex: 1 }}>{sakelar.label}</Typography>
            <Switch size="small" disabled={disabled} checked={tampil[sakelar.kunci] !== false} onChange={(e) => setTampil((t) => ({ ...t, [sakelar.kunci]: e.target.checked }))} />
          </Box>
        )}
        {petunjukBlok[blok] && <Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>{petunjukBlok[blok]}</Typography>}
      </Paper>
    );
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={skema.judul}
        subheader="Geser untuk mengubah urutan blok dan baris (atau pakai tombol panah). Sakelar menyembunyikan bagian yang tidak perlu dicetak."
        action={(
          <Button size="small" startIcon={<ResetIcon />} disabled={disabled} onClick={() => setUrutan(skema.bawaan)}>
            Urutan bawaan
          </Button>
        )}
      />
      <CardContent>
        <DaftarSortable
          items={urutan.blok}
          onChange={(daftar) => setUrutan((p) => ({ ...p, blok: daftar }))}
          render={(blok, p, g) => isiBlok(blok, { pegangan: p, ...g })}
        />
        {setLogoStruk && !adaLogo && <Typography variant="caption" color="text.secondary">Logo cabang belum diunggah (menu Konfigurasi), jadi belum tercetak.</Typography>}
      </CardContent>
    </Card>
  );
};
